// Building The Bridge – 16-week program blueprint content.

export const STAGES = [
  { id: "discover", name: "Discover", weeks: "Weeks 0–3", color: "#8C6B5D",
    goal: "Uncover patterns, values, strengths, credibility, and the real motivation for this transition." },
  { id: "distil", name: "Distil", weeks: "Weeks 3–6", color: "#C48A71",
    goal: "Condense broad experience into a distinctive advisory identity and point of view." },
  { id: "translate", name: "Translate", weeks: "Weeks 6–9", color: "#A98467",
    goal: "Turn internal clarity into an external consulting proposition." },
  { id: "test", name: "Test", weeks: "Weeks 9–12", color: "#697A76",
    goal: "Gather market evidence and refine the offer based on real conversations." },
  { id: "build", name: "Build", weeks: "Weeks 12–16", color: "#615A56",
    goal: "Create a sustainable consulting model, operating rhythm, and 90-day roadmap." },
];

export const WEEKS = [
  { week: 0, focus: "Intake, baseline, readiness", output: "Intake form, goals, baseline confidence scores", stage: "discover", session: 0 },
  { week: 1, focus: "Session 1: Discover foundations", output: "Values map, transition goal, contribution framing", stage: "discover", session: 1 },
  { week: 2, focus: "Reflection and evidence gathering", output: "Career timeline, energy audit, feedback scan", stage: "discover" },
  { week: 3, focus: "Session 2: Distil value", output: "Signature themes, positioning directions", stage: "distil", session: 2 },
  { week: 4, focus: "Distillation work", output: "Credibility bank, proof points, professional narrative", stage: "distil" },
  { week: 5, focus: "Positioning refinement", output: "Draft positioning statement, value proposition", stage: "distil" },
  { week: 6, focus: "Session 3: Translate to offer", output: "Ideal client, problem statement, offer hypotheses", stage: "translate", session: 3 },
  { week: 7, focus: "Offer and messaging development", output: "One-page offer brief, audience shortlist", stage: "translate" },
  { week: 8, focus: "Outreach prep", output: "Conversation list, outreach scripts, intro language", stage: "translate" },
  { week: 9, focus: "Session 4: Test strategy", output: "Market test plan, pilot design, pricing hypothesis", stage: "test", session: 4 },
  { week: 10, focus: "Real-world testing", output: "3–5 market conversations", stage: "test" },
  { week: 11, focus: "Offer refinement", output: "Insight summary, revised offer, proposal draft", stage: "test" },
  { week: 12, focus: "Session 5: Build the practice", output: "Sustainable practice model, boundaries, visibility rhythm", stage: "build", session: 5 },
  { week: 13, focus: "Practice design work", output: "Offer structure, pricing logic, operating model", stage: "build" },
  { week: 14, focus: "Asset creation", output: "Bio, service description, proposal or web copy", stage: "build" },
  { week: 15, focus: "Session 6: Integrate and commit", output: "90-day roadmap, launch priorities, risk plan", stage: "build", session: 6 },
  { week: 16, focus: "Wrap-up and activation", output: "First outreach actions, post-program accountability plan", stage: "build" },
];

// AI action presets available inline in worksheets.
export const AI_ACTIONS = {
  summarize: { label: "Summarize reflections", task: "summarize" },
  sharpen: { label: "Sharpen positioning", task: "sharpen-positioning" },
  proof: { label: "Make proof points", task: "proof-points" },
  offer: { label: "Draft an offer", task: "draft-offer" },
  outreach: { label: "Draft outreach", task: "draft-outreach" },
  bio: { label: "Draft bio", task: "draft-bio" },
  conversations: { label: "Analyze conversations", task: "analyze-conversations" },
  plan: { label: "Build weekly plan", task: "weekly-plan" },
};

export const SESSIONS = [
  {
    id: 1, title: "Discover Foundations", week: 1, stage: "discover",
    purpose: "Establish the transformation goal, surface Katerina's values and contribution themes, and create the frame for the entire journey.",
    structure: [
      "Welcome and frame the work",
      "Clarify what \u201cBuilding The Bridge\u201d means for Katerina",
      "Explore transition tension: contribution vs commercialization, identity vs positioning, sustainability vs self-sacrifice",
      "Review intake and baseline confidence scores",
      "Begin values, contribution, and experience mapping",
      "Define the first milestone and between-session work",
    ],
    exercises: [
      { id: "bridge-baseline", title: "Bridge Baseline", ai: "summarize",
        prompts: ["Where am I now?", "What am I moving toward?", "What feels unclear, charged, or risky?"] },
      { id: "values-map", title: "Values and Non-Negotiables Map", ai: "summarize",
        prompts: ["What must remain true in my future practice?", "What would make success feel hollow?"] },
      { id: "contribution-inventory", title: "Contribution Inventory", ai: "summarize",
        prompts: ["In past roles, what changed because I was there?", "What did people trust me to hold, shape, or solve?"] },
      { id: "sustainability-def", title: "Sustainability Definition", ai: "summarize",
        prompts: ["What does sustainable mean financially, emotionally, creatively, and relationally?"] },
    ],
    aiPrompts: [
      "Read my intake notes and summarize the top 5 tensions I am trying to resolve in this transition.",
      "Based on these reflections, identify recurring values and non-negotiables in how I want to work.",
      "Turn this contribution inventory into a short summary of the kinds of value I consistently create.",
      "What appears to matter more to me than title or status in this next chapter?",
    ],
    coachPrompts: [
      "What are you trying to preserve as you move into consulting?",
      "Where in your career have you already been acting like an advisor, even if it was not named that way?",
      "What kind of contribution would feel deeply aligned, and what kind would feel extractive or performative?",
      "If this experience works, what will be true in 16 weeks that is not true now?",
      "What are you afraid consulting might force you to become?",
    ],
    activities: [
      "Map your transferable skills, from your current audience to your desired audience",
      "Complete a career timeline with 8–10 pivotal moments",
      "Complete a 2-week energy audit: energizing work, draining work, meaningful work",
      "Gather 5 feedback reflections from trusted peers or former colleagues",
      "Write a one-page reflection: \u201cWhat I want consulting to make possible\u201d",
    ],
    milestone: { n: 1, title: "Pattern Clarity Started",
      desc: "Katerina can name her core values, key tensions, and the first visible patterns in her work history." },
    deliverables: ["Baseline assessment", "Transferable skills map", "Values and non-negotiables map", "Initial contribution summary", "Career timeline template completed"],
  },
  {
    id: 2, title: "Distil Signature Value", week: 3, stage: "distil",
    purpose: "Convert Katerina's experience into a distinctive advisory identity, signature themes, and early positioning language.",
    structure: [
      "Review career timeline, energy audit, and peer feedback",
      "Surface recurring themes across her body of work",
      "Distinguish strengths from market value",
      "Define 2–3 advisory themes or domains",
      "Draft initial positioning directions",
      "Agree what should be tested further",
    ],
    exercises: [
      { id: "theme-clustering", title: "Theme Clustering", ai: "summarize",
        prompts: ["Group career examples into recurring advisory capabilities."] },
      { id: "credibility-bank", title: "Credibility Bank", ai: "proof",
        prompts: ["List projects, roles, outcomes, recognition, partnerships, and strategic wins."] },
      { id: "especially-i", title: "Especially I Exercise", ai: "summarize",
        prompts: ["What do I do in a way shaped by my lived experience, not just generic skill?"] },
      { id: "proof-point-mapping", title: "Proof Point Mapping", ai: "proof",
        prompts: ["For each theme, what evidence shows I can do this credibly?"] },
    ],
    aiPrompts: [
      "Review this timeline and feedback set, then identify the 3 strongest advisory themes that connect my experience.",
      "Draft 3 positioning statements for me as a consultant serving the creative and cultural sector.",
      "Convert these achievements into proof points that sound relevant to clients, not just employers.",
      "What words or phrases in my writing feel distinctive and worth carrying into my positioning?",
    ],
    coachPrompts: [
      "What do people trust you to help them make sense of?",
      "Which of your strengths are real, but not central to your consulting edge?",
      "What is the deeper pattern underneath your roles and titles?",
      "What problem do you seem built to help people navigate?",
      "Where is your credibility strongest: strategy, facilitation, partnerships, cultural leadership, change, programming, funding, or something else?",
    ],
    activities: [
      "Create a credibility bank with 10–15 proof points",
      "Write a 200-word professional narrative: the thread that connects your work",
      "Test 3 positioning statements with 3 trusted contacts",
      "Highlight which language feels true, forced, clear, or vague",
    ],
    milestone: { n: 2, title: "Value Clarity",
      desc: "Katerina can articulate the thread connecting her experience and name 2–3 credible advisory themes." },
    deliverables: ["Signature advisory themes", "Credibility bank", "Draft professional narrative", "2–3 draft positioning statements"],
  },
  {
    id: 3, title: "Translate Experience into a Consulting Offer", week: 6, stage: "translate",
    purpose: "Identify who Katerina is best positioned to help first, what problem she can solve, and what forms her consulting could take.",
    structure: [
      "Review positioning feedback and narrative drafts",
      "Identify likely first clients and sectors of fit",
      "Translate experience into urgent client problems",
      "Design 1–2 consulting offer hypotheses",
      "Define outcomes, format, and scope",
      "Prepare for market testing",
    ],
    exercises: [
      { id: "ideal-client-matrix", title: "Ideal Client Matrix", ai: "summarize",
        prompts: ["Who is most likely to hire me first?", "Where do I have trust, relevance, and access?"] },
      { id: "problem-translation", title: "Problem Translation Canvas", ai: "sharpen",
        prompts: ["What are clients actually struggling with?", "How does my experience help solve it?"] },
      { id: "offer-architecture", title: "Offer Architecture Canvas", ai: "offer",
        prompts: ["Advisory, sprint, workshop, facilitation, strategy support, fractional role?"] },
      { id: "outcome-ladder", title: "Outcome Ladder", ai: "summarize",
        prompts: ["What short-term outcome can I credibly help a client achieve?"] },
    ],
    aiPrompts: [
      "Based on my experience, suggest 3 possible ideal client profiles within or adjacent to the creative sector.",
      "Rewrite my language so it speaks to a buyer with a real organizational problem, not just a peer in the field.",
      "Generate 3 offer concepts I could test first: one strategic, one facilitative, and one advisory.",
      "Draft a one-paragraph description of a consulting offer using clear client-facing language.",
    ],
    coachPrompts: [
      "Who is most likely to say yes first, not eventually?",
      "What problem feels urgent enough that someone would pay for your help in the next 3 months?",
      "Which offer format best fits your strengths and the way you like to work?",
      "Where might your language still be too conceptual or sector-insider?",
      "What would a values-aligned first offer look like if it was simple, useful, and credible?",
    ],
    activities: [
      "Choose one primary client segment and one secondary segment",
      "Draft 1–2 one-page offer briefs",
      "Write a short introduction script: \u201cI help…\u201d",
      "Build a list of 10 potential market conversations",
      "Draft outreach notes for those conversations",
    ],
    milestone: { n: 3, title: "Market and Offer Clarity",
      desc: "Katerina has a clear client hypothesis, a problem statement, and at least one offer worth testing." },
    deliverables: ["Ideal client profile", "Problem statement", "Offer hypothesis 1 and 2", "One-page offer brief", "Intro script and outreach list"],
  },
  {
    id: 4, title: "Test the Offer in the Real World", week: 9, stage: "test",
    purpose: "Prepare Katerina to run meaningful market conversations, gather evidence, refine messaging, and shape a first pilot or proposal.",
    structure: [
      "Review outreach readiness and emotional resistance",
      "Finalize conversation goals and scripts",
      "Define what data to collect from market conversations",
      "Clarify pricing hypothesis and pilot shape",
      "Design the first proposal or pilot offer",
      "Set testing targets for the next 3 weeks",
    ],
    exercises: [
      { id: "conversation-intent", title: "Conversation Intent Sheet", ai: "summarize",
        prompts: ["What am I trying to learn from each conversation?"] },
      { id: "resonance-tracker", title: "Resonance Tracker", ai: "conversations",
        prompts: ["Which language gets energy, curiosity, or confusion?"] },
      { id: "objection-log", title: "Objection Log", ai: "conversations",
        prompts: ["What concerns come up around relevance, scope, budget, or fit?"] },
      { id: "pilot-design", title: "Pilot Design Canvas", ai: "offer",
        prompts: ["What is the smallest meaningful engagement that proves value?"] },
    ],
    aiPrompts: [
      "Create a market conversation guide based on my offer and target client.",
      "Draft 3 outreach messages: warm contact, loose connection, and direct but respectful introduction.",
      "Turn this offer into a small pilot scope with clear outcomes, timeline, and deliverables.",
      "Based on these conversation notes, summarize what buyers seem to care about most.",
    ],
    coachPrompts: [
      "What are you hoping the market will confirm, and what are you afraid it might challenge?",
      "What would count as a useful test even if nobody buys immediately?",
      "Where is your current language most alive and where is it still abstract?",
      "What is the smallest paid engagement that would feel both real and manageable?",
      "How can you test without overpromising or overcustomizing?",
    ],
    activities: [
      "Conduct 3–5 market conversations",
      "Track responses using the resonance tracker",
      "Revise the offer based on recurring patterns",
      "Draft and, if appropriate, send one proposal or pilot outline",
      "Write a short reflection after each conversation: what landed, what didn't, what surprised me",
    ],
    milestone: { n: 4, title: "Evidence of Resonance",
      desc: "Katerina has real external feedback on her messaging, audience, and offer." },
    deliverables: ["Market conversation notes", "Resonance and objection summary", "Refined offer language", "Pilot scope or proposal draft", "Initial pricing hypothesis"],
  },
  {
    id: 5, title: "Build the Practice Around the Offer", week: 12, stage: "build",
    purpose: "Turn the tested offer into a sustainable practice model with healthy boundaries, clear priorities, and a realistic growth rhythm.",
    structure: [
      "Review findings from conversations or pilot",
      "Decide what to keep, refine, or stop",
      "Define sustainable service structure and pricing logic",
      "Set boundaries around scope, availability, and client fit",
      "Design a 90-day business development rhythm",
      "Clarify visibility strategy and practice infrastructure",
    ],
    exercises: [
      { id: "sustainable-practice", title: "Sustainable Practice Canvas", ai: "summarize",
        prompts: ["What I offer", "Who I serve", "How much I can hold", "What I need financially", "What supports creative and emotional sustainability"] },
      { id: "client-fit-filter", title: "Client Fit Filter", ai: "summarize",
        prompts: ["Which clients are aligned, draining, extractive, or off-mission?"] },
      { id: "boundary-design", title: "Boundary Design Exercise", ai: "summarize",
        prompts: ["What do I include, exclude, and protect?"] },
      { id: "visibility-rhythm", title: "Visibility Rhythm Map", ai: "plan",
        prompts: ["Relationship-building, thought leadership, partnerships, speaking, referrals"] },
    ],
    aiPrompts: [
      "Turn this offer into 2 or 3 service packages with different levels of depth.",
      "Help me write client-facing language that reflects rigor and values without sounding vague.",
      "Create a simple weekly business development rhythm for a consultant who wants to stay rooted in the creative sector.",
      "Draft a short services page or PDF copy from my offer notes.",
    ],
    coachPrompts: [
      "What does enough look like for you in this practice?",
      "How do we make this commercially viable without disconnecting from contribution?",
      "What kinds of clients or requests would slowly erode your values if you said yes too often?",
      "What offer structure supports your strengths instead of recreating burnout?",
      "What can stay intentionally small in your practice?",
    ],
    activities: [
      "Finalize service structure and pricing range",
      "Draft a services page, PDF, or short capability statement",
      "Create a client fit checklist",
      "Build a 12-week relationship and visibility plan",
      "Begin one weekly visibility action or outreach rhythm",
    ],
    milestone: { n: 5, title: "Practice Foundation",
      desc: "Katerina has a workable model for how the consulting practice will operate." },
    deliverables: ["Sustainable practice canvas", "Pricing and package logic", "Client fit filter", "Service description asset", "90-day visibility and relationship plan"],
  },
  {
    id: 6, title: "Integrate, Commit, and Cross the Bridge", week: 15, stage: "build",
    purpose: "Consolidate the journey, finalize key assets, identify risks, and commit to the next 90 days of practice-building.",
    structure: [
      "Reflect on transformation from week 1 to week 15",
      "Finalize positioning, offer, client, and practice model",
      "Identify remaining fears, risks, and decision points",
      "Build a 90-day action roadmap",
      "Define measures of progress and accountability",
      "Close the engagement with a strong transition plan",
    ],
    exercises: [
      { id: "before-after", title: "Before / After Narrative", ai: "summarize",
        prompts: ["Who was I at the start?", "What can I now claim with confidence?"] },
      { id: "bridge-forward", title: "Bridge Forward Roadmap", ai: "plan",
        prompts: ["What are the next 3 priorities?", "What must happen in the next 30, 60, and 90 days?"] },
      { id: "risk-resilience", title: "Risk and Resilience Plan", ai: "summarize",
        prompts: ["What might derail me?", "What support structures do I need?"] },
      { id: "practice-charter", title: "Practice Charter", ai: "summarize",
        prompts: ["What is the purpose of this practice and how will I protect it?"] },
    ],
    aiPrompts: [
      "Write a concise consultant bio based on my final positioning and offer.",
      "Turn my final offer into a website-ready summary with clear outcomes and audience fit.",
      "Translate my 90-day roadmap into weekly actions and accountability check-ins.",
      "Summarize the evolution of my thinking from week 1 to now in a way that reinforces confidence.",
    ],
    coachPrompts: [
      "What are you now able to say clearly that you could not say at the start?",
      "What are you ready to claim as your consulting edge?",
      "What forms of contribution are you now choosing on purpose?",
      "What will protect you from drifting back into unclear or underpriced work?",
      "What is the next concrete proof point you need in the next 30 days?",
    ],
    activities: [
      "Send first 5 targeted outreach or follow-up messages",
      "Finalize one outward-facing asset: service PDF, short landing page copy, or LinkedIn/About rewrite",
      "Schedule post-program accountability checkpoints",
      "Start executing the 90-day roadmap",
    ],
    milestone: { n: 6, title: "Launch Readiness",
      desc: "Katerina is ready to operate as a consultant with clarity, evidence, and a next-step plan." },
    deliverables: ["Final positioning statement", "Final client and offer summary", "Consultant bio and service copy", "90-day roadmap", "Accountability structure and measurement dashboard"],
  },
];

export const MILESTONES = [
  { n: 1, title: "Pattern Clarity", evidence: ["Values named", "Tensions surfaced", "Contribution patterns visible"] },
  { n: 2, title: "Value Clarity", evidence: ["2–3 advisory themes identified", "Proof points collected", "Narrative thread is visible"] },
  { n: 3, title: "Market and Offer Clarity", evidence: ["Target client chosen", "Problem statement drafted", "At least one offer defined"] },
  { n: 4, title: "Evidence of Resonance", evidence: ["Real conversations completed", "Messaging tested", "Pilot or proposal shaped"] },
  { n: 5, title: "Practice Foundation", evidence: ["Pricing logic, boundaries, and operating rhythm defined", "Values and sustainability translated into design choices"] },
  { n: 6, title: "Launch Readiness", evidence: ["Outward-facing language complete", "90-day roadmap active", "Next opportunities identified"] },
];

export const SUCCESS_MEASURES = [
  { id: "positioning_clarity", label: "Clarity of positioning", type: "scale" },
  { id: "confidence_value", label: "Confidence describing value", type: "scale" },
  { id: "confidence_charging", label: "Confidence charging for work", type: "scale" },
  { id: "ideal_client_clarity", label: "Clarity of ideal client", type: "scale" },
  { id: "values_alignment", label: "Alignment between values and business model", type: "scale" },
  { id: "conversations_count", label: "Market conversations completed", type: "count" },
  { id: "proposals_count", label: "Proposals or pilots created", type: "count" },
];

export const CHECKPOINTS = [
  { id: "week0", label: "Week 0" },
  { id: "week8", label: "Week 8" },
  { id: "week16", label: "Week 16" },
];

export const CHECKIN_QUESTIONS = [
  "What became clearer this week?",
  "What felt resistant or emotionally charged?",
  "What language felt true?",
  "What did the market reflect back?",
  "What support do I need before the next step?",
];

export const WEEKLY_RHYTHM = [
  { when: "Early week", what: "Reflection or writing task" },
  { when: "Midweek", what: "AI synthesis and refinement" },
  { when: "Late week", what: "Action step in the real world" },
  { when: "End of week", what: "Short check-in and learning note" },
];

export const PILOT_RISKS = [
  { title: "Breadth may make positioning difficult", watch: "Too many themes, no clear first client.", response: "Choose a strong entry point, not a lifetime label." },
  { title: "Values alignment may become a reason to avoid pricing", watch: "Language like \u201cI just want to be useful\u201d replacing commercial clarity.", response: "Reframe pricing as enabling sustained contribution." },
  { title: "Sector-rooted work may be described too internally", watch: "Language that peers understand but buyers do not.", response: "Constantly translate into client problem, risk, and outcome language." },
  { title: "The first offer may be too bespoke", watch: "Over-customized offers that are hard to repeat.", response: "Design a clear pilot format with enough structure to reuse." },
  { title: "Testing may trigger identity discomfort", watch: "Avoidance of outreach, perfectionism, or endless refinement.", response: "Normalize testing as inquiry, not self-exposure." },
];

export const COACH_WATCH = [
  "Under-claiming expertise",
  "Over-identifying with service and underpricing value",
  "Resisting specificity because breadth feels safer",
  "Staying in reflection too long and delaying testing",
  "Designing an offer that sounds good but does not fit her energy or style",
];

export const INTAKE_FIELDS = [
  { id: "current_role", label: "Where are you now? (current role / situation)" },
  { id: "transition_goal", label: "What are you moving toward? (transition goal)" },
  { id: "motivation", label: "Why this transition, and why now?" },
  { id: "fears", label: "What feels unclear, charged, or risky?" },
  { id: "success_definition", label: "What would make this 16 weeks a success for you?" },
];

export const DELIVERABLES_CHECKLIST = [
  "Intake baseline and transition goal",
  "Values and non-negotiables map",
  "Transferable skills map",
  "Career timeline and contribution inventory",
  "Signature advisory themes",
  "Credibility bank",
  "Professional narrative",
  "Positioning statement",
  "Ideal client profile",
  "Problem statement",
  "1–2 consulting offer briefs",
  "Outreach script and market conversation guide",
  "Conversation notes and resonance summary",
  "Pilot scope or proposal draft",
  "Pricing logic",
  "Sustainable practice canvas",
  "Client fit filter",
  "Service description asset",
  "Consultant bio",
  "90-day roadmap",
  "Personal practice charter",
];

export function stageOf(id) {
  return STAGES.find((s) => s.id === id);
}

// Intake is complete when every intake question has an answer.
export function isIntakeComplete(getEntry) {
  return INTAKE_FIELDS.every((f) => String(getEntry(`intake:${f.id}`, "")).trim());
}
